import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ZodError } from 'zod';

import { ApiNoLiquidityAvailableError } from '@/lib/errors/api';
import { createJupiterApiError, JupiterApiError } from '@/lib/errors/jupiter';

import { CHAIN_IDS } from '@/constants/chain';

import { SolanaSwapQuote } from '@/utils/0x.types';
import { isChainAddress } from '@/utils/models';
import { routes } from '@/utils/routes';
import { ClassifierToken } from '@/utils/slippage/classifier';
import { withSentSlippage } from '@/utils/slippage/sentSlippage';
import { getSolanaWalletMaxTransactionVersion } from '@/utils/solana';

import { useMatchaWallets } from './useMatchaWallets';
import { useResolvedSlippage } from './useResolvedSlippage';
import { SwapArgs } from './useSwap';

const jupiterApiFetcher = async (url: string, isAuto: boolean): Promise<SolanaSwapQuote> => {
  const response = await fetch(url);
  if (response.status === 204) throw new ApiNoLiquidityAvailableError();
  const data = await response.json();
  // The request might be successful but it might contain an error that we need to show the user
  // This is a way to report some user facing errors, but not track them in Sentry
  if ('error' in data && 'errorCode' in data) throw createJupiterApiError(data);
  if (!response.ok) throw new Error(data.message);
  return withSentSlippage(data, url, isAuto);
};

export type SolanaSwapQuoteQueryOptions = Omit<
  UseQueryOptions<SolanaSwapQuote, JupiterApiError | ZodError | Error>,
  'queryKey' | 'queryFn'
>;

const solanaSwapQuoteQueryKey = (url: string | null) => ['solana-swap-quote', url] as const;

export function useSolanaSwapQuote(
  query: SwapArgs | null,
  options: SolanaSwapQuoteQueryOptions,
): UseQueryResult<SolanaSwapQuote, JupiterApiError | ZodError | Error> {
  // Get the Solana wallet address from useMatchaWallets
  const { solanaWallet } = useMatchaWallets();
  const sellTokenAddress = query?.sellTokenAddress;
  const buyTokenAddress = query?.buyTokenAddress;
  const sellToken = useMemo<ClassifierToken | undefined>(
    () => (isChainAddress(sellTokenAddress) ? { address: sellTokenAddress } : undefined),
    [sellTokenAddress],
  );
  const buyToken = useMemo<ClassifierToken | undefined>(
    () => (isChainAddress(buyTokenAddress) ? { address: buyTokenAddress } : undefined),
    [buyTokenAddress],
  );
  const { bps, isAuto, isReady } = useResolvedSlippage({
    chainId: CHAIN_IDS.SOLANA,
    sellToken,
    buyToken,
  });

  const url = useMemo(() => {
    if (
      !query ||
      !isReady ||
      parseInt(query.sellAmount, 10) === 0 || // don't fetch a zero amount
      query.sellTokenChainId !== CHAIN_IDS.SOLANA ||
      query.buyTokenChainId !== CHAIN_IDS.SOLANA
    ) {
      return null;
    }

    const params = new URLSearchParams({
      sellTokenAddress: query.sellTokenAddress,
      buyTokenAddress: query.buyTokenAddress,
      sellAmount: query.sellAmount,
      dynamicSlippage: String(isAuto),
      slippageBps: String(bps),
      maxTransactionVersion: String(
        solanaWallet ? getSolanaWalletMaxTransactionVersion(solanaWallet) : 1,
      ),
    });
    if (solanaWallet) {
      params.set('userPublicKey', solanaWallet.address);
    }

    return `${routes.api.SOLANA_SWAP_QUOTE}?${params.toString()}`;
  }, [query, isReady, isAuto, bps, solanaWallet]);

  const { enabled: callerEnabled = true, ...restOptions } = options;
  return useQuery<SolanaSwapQuote, JupiterApiError | ZodError | Error>({
    queryKey: solanaSwapQuoteQueryKey(url),
    queryFn: () => jupiterApiFetcher(url!, isAuto),
    ...restOptions,
    // Computed last so it overrides any `enabled` in `restOptions`; we
    // AND-merge so the leaf still self-disables when the URL guard fails
    // even if the caller passes `enabled: true`.
    enabled: url !== null && callerEnabled,
  });
}
